/**
 * Pseudo-localisation (Deliverable D/E) — L5's Machine lane.
 *
 * Pseudo-localisation accents and expands every literal character of a message while
 * leaving ICU syntax (`{…}`, `#`) untouched, so we can prove layouts survive ~40% string
 * growth and non-ASCII glyphs *before* any real translation exists. It is a derived
 * `Catalog`: same keys, transformed patterns — demonstrating the catalog-only model.
 */
const ACCENT_MAP = {
    a: ' á', b: 'b̃', c: 'ç', d: 'ð', e: 'é', f: 'ƒ', g: 'ǵ', h: 'h̃', i: 'í',
    j: 'ǰ', k: 'ǩ', l: 'ĺ', m: 'm̃', n: 'ñ', o: 'ó', p: 'ṕ', q: 'q̃', r: 'ŕ',
    s: 'š', t: 'ţ', u: 'ú', v: 'ṽ', w: 'ẃ', x: 'x̃', y: 'ý', z: 'ž',
    A: 'Á', B: 'B̃', C: 'Ç', D: 'Ð', E: 'É', F: 'F̃', G: 'Ǵ', H: 'H̃', I: 'Í',
    J: 'Ĵ', K: 'Ǩ', L: 'Ĺ', M: 'M̃', N: 'Ñ', O: 'Ó', P: 'Ṕ', Q: 'Q̃', R: 'Ŕ',
    S: 'Š', T: 'Ţ', U: 'Ú', V: 'Ṽ', W: 'Ẃ', X: 'X̃', Y: 'Ý', Z: 'Ž',
};
/**
 * Transform a single ICU pattern: accent the literal letters, pad to force ~40% growth,
 * and wrap in brackets so truncation is visible — but never touch the bytes between `{`
 * and `}` (ICU argument syntax) or a bare `#`.
 */
export function pseudoPattern(pattern) {
    let out = '';
    let depth = 0;
    for (const ch of pattern) {
        if (ch === '{') {
            depth += 1;
            out += ch;
        }
        else if (ch === '}') {
            depth = Math.max(0, depth - 1);
            out += ch;
        }
        else if (depth > 0) {
            out += ch; // inside ICU argument: leave untouched
        }
        else {
            out += ACCENT_MAP[ch] ?? ch;
        }
    }
    // Expansion padding (outside any text the reader must parse): brackets + filler.
    return `⟦${out}··⟧`;
}
/** Build a pseudo-locale `Catalog` from a source catalog (default source: English). */
export function makePseudoCatalog(source, locale = 'en-XA') {
    const messages = {};
    for (const [key, pattern] of Object.entries(source.messages)) {
        messages[key] = pseudoPattern(pattern);
    }
    return { locale, dir: source.dir, messages };
}
// RTL pseudo-locale controls.
const RLI = '⁧'; // RIGHT-TO-LEFT ISOLATE (U+2067)
const PDI = '⁩'; // POP DIRECTIONAL ISOLATE (U+2069)
/**
 * Build an RTL pseudo-locale `Catalog` (conventionally `ar-XB`): accent + expand like the
 * LTR pseudo, but force right-to-left rendering by wrapping each message in RLI…PDI and
 * declaring `dir: 'rtl'`. This stress-tests the *direction flip* — mirrored layout, logical
 * CSS properties, bidi isolation of technical ids — before the real Arabic catalog is
 * trusted, and gives Agent 2's bidi CI lane deterministic data.
 */
export function makeRtlPseudoCatalog(source, locale = 'ar-XB') {
    const messages = {};
    for (const [key, pattern] of Object.entries(source.messages)) {
        messages[key] = `${RLI}${pseudoPattern(pattern)}${PDI}`;
    }
    return { locale, dir: 'rtl', messages };
}
//# sourceMappingURL=pseudo.js.map